import { useEffect, useRef } from "react";
import { toast } from "sonner";
import api from "../lib/api";

const MODULE_LABELS = {
  kitchen: "Carta y cocina",
  stock: "Almacén y compras",
  sales: "Ventas y caja",
  finance: "Finanzas y análisis",
  digital: "Presencia digital",
};

const NUDGE_COPY = {
  sales: {
    icon: "🧾",
    title: "Llevas una semana usando MenuGenius",
    body: "¿Quieres activar Ventas y caja para empezar a registrar tu día?",
  },
  finance: {
    icon: "📊",
    title: "Llevas un mes con MenuGenius",
    body: "Tienes datos suficientes para Finanzas y análisis. ¿Lo activamos?",
  },
  digital: {
    icon: "🌐",
    title: "Tu negocio está madurando",
    body: "Activa Presencia digital para vender online y crecer en redes.",
  },
  kitchen: {
    icon: "👨‍🍳",
    title: "¿Activamos Carta y cocina?",
    body: "Configura platos, recetas y producción para empezar a controlar costes.",
  },
  stock: {
    icon: "📦",
    title: "¿Activamos Almacén y compras?",
    body: "Lleva el inventario, gestiona pedidos a proveedores y previsiones.",
  },
};

const SHOWN_KEY = "mg_module_unlock_shown_v1";

function loadShown() {
  try { return new Set(JSON.parse(localStorage.getItem(SHOWN_KEY) || "[]")); }
  catch { return new Set(); }
}
function saveShown(set) {
  try { localStorage.setItem(SHOWN_KEY, JSON.stringify(Array.from(set))); }
  catch { /* ignore */ }
}

/**
 * Fires a one-time toast suggesting to enable a sidebar group when the
 * onboarding-gradual default for the restaurant's age would unlock it but
 * the owner hasn't activated nor dismissed it.
 *
 * Per device throttling: each suggestion is shown once per restaurant per
 * device (localStorage). The "No, gracias" CTA persists the dismissal on
 * the server so it never appears again across devices.
 */
export default function useModuleUnlockNudge({ restaurantId, role }) {
  const firedRef = useRef(false);

  useEffect(() => {
    if (!restaurantId || role !== "owner" || firedRef.current) return;
    firedRef.current = true;

    let cancelled = false;
    (async () => {
      try {
        const res = await api.request(`/restaurants/${restaurantId}/sidebar-modules`);
        if (cancelled) return;
        const suggested = (res?.suggested_unlocks || []).filter(
          (k) => MODULE_LABELS[k],
        );
        if (suggested.length === 0) return;

        const shownSet = loadShown();
        const next = suggested.find((k) => !shownSet.has(`${restaurantId}:${k}`));
        if (!next) return;

        const copy = NUDGE_COPY[next] || {
          icon: "✨",
          title: `¿Activamos ${MODULE_LABELS[next]}?`,
          body: "Tu negocio ya tiene datos suficientes para esta sección.",
        };

        const enableModule = async () => {
          try {
            const cur = await api.request(`/restaurants/${restaurantId}/sidebar-modules`);
            const enabled = new Set(cur?.enabled_modules || []);
            enabled.add(next);
            await api.request(
              `/restaurants/${restaurantId}/sidebar-modules`,
              { method: "PUT", body: JSON.stringify({ enabled_modules: Array.from(enabled) }) },
            );
            toast.success(`${MODULE_LABELS[next]} activado en el menú lateral`);
            // Reload sidebar by triggering a soft refresh of the page state
            window.dispatchEvent(new Event("sidebar-modules-changed"));
          } catch {
            toast.error("No se pudo activar el módulo");
          }
        };

        const dismissForever = async () => {
          try {
            await api.request(
              `/restaurants/${restaurantId}/sidebar-modules/dismiss-unlock`,
              { method: "POST", body: JSON.stringify({ module_key: next }) },
            );
          } catch { /* ignore */ }
        };

        toast(
          `${copy.icon} ${copy.title}`,
          {
            description: copy.body,
            duration: 12000,
            action: {
              label: "Activar",
              onClick: () => { enableModule(); },
            },
            cancel: {
              label: "No, gracias",
              onClick: () => { dismissForever(); },
            },
          },
        );

        shownSet.add(`${restaurantId}:${next}`);
        saveShown(shownSet);
      } catch { /* ignore */ }
    })();

    return () => { cancelled = true; };
  }, [restaurantId, role]);
}
